import { useState } from 'react';

/**
 * ホバー／押下でスタイルが変わる要素。
 *
 * インライン style を onMouseEnter/onMouseLeave で直接書き換えると、iOS では
 * タップ時に mouseenter 相当だけが発火して mouseleave が来ないことがあり、
 * ハイライトが残り続ける。ここでは Pointer Events を使い、
 * **pointerType が 'mouse' のときだけ**ホバー状態にすることで、タッチでは
 * 構造的にホバーが起きないようにしている（mouseleave の発火に依存しない）。
 *
 * pointercancel の処理は必須。iOS はスクロールジェスチャを認識した瞬間にこれを
 * 投げるので、拾わないと「スクロールしただけで行が押下色のまま残る」という
 * 同じ症状が押下側で再発する。
 *
 * Props:
 *   as          — レンダリングするタグ（既定 'div'）
 *   style       — 基本スタイル
 *   hoverStyle  — マウスでホバー中に重ねるスタイル
 *   activeStyle — 押下中に重ねるスタイル（省略時は hoverStyle を流用）
 */
export default function Pressable({
  as: Tag = 'div',
  style,
  hoverStyle,
  activeStyle,
  children,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
  ...rest
}) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const overlay = active ? (activeStyle ?? hoverStyle) : hover ? hoverStyle : null;

  // as="img" のような void 要素にも使うため、children が無いときは子を渡さない
  const inner = children === undefined ? {} : { children };

  return (
    <Tag
      {...inner}
      {...rest}
      style={overlay ? { ...style, ...overlay } : style}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') setHover(true);
        onPointerEnter?.(e);
      }}
      onPointerLeave={(e) => {
        setHover(false);
        setActive(false);
        onPointerLeave?.(e);
      }}
      onPointerDown={(e) => {
        setActive(true);
        onPointerDown?.(e);
      }}
      onPointerUp={(e) => {
        setActive(false);
        onPointerUp?.(e);
      }}
      onPointerCancel={(e) => {
        setActive(false);
        setHover(false);
        onPointerCancel?.(e);
      }}
    />
  );
}
